import React, { useState, useEffect } from 'react';
import {
    Form,
    Input,
    Select,
    Row,
    Col,
    Button,
    Space,
    Modal,
    Upload,
} from 'antd';

import { PlusOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { apiRequest } from '../../../../service/apiRequest';
import { showSuccess, showError } from '../../../../components/errorHandler';

import { FPs } from "../../../../types/FabricPatterns";

interface FtModalProps {
    visible: boolean;
    onCancel: () => void;
    onCreate: (data: any) => void; // Callback after successful submit
    initialValues?: FPs | null;
    isEditMode?: boolean;
    fabricpatterndata: FPs[];
}

const FtModal: React.FC<FtModalProps> = ({
    visible,
    onCancel,
    onCreate,
    initialValues = null,
    isEditMode = false,
    fabricpatterndata,
}) => {
    const [form] = Form.useForm();
    const [isModalClose, setIsModalClose] = useState(true);

    const [imageFile, setImageFile] = useState<File | null>(null);
    const [profileImageFileList, setProfileImageFileList] = useState<UploadFile[]>([]);


    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const [previewTitle, setPreviewTitle] = useState('');


    // Determine if submit button should be disabled based on form validation
    const isSubmitDisabled = () => {
        const errors = form.getFieldsError();
        return errors.some(({ errors }) => errors.length > 0);
    };


    // image preview 
    const handlePreview = async (file: UploadFile) => {
        if (!file.url && !file.preview) {
            file.preview = await getBase64(file.originFileObj as File);
        }

        setPreviewImage(file.url || (file.preview as string));
        setPreviewOpen(true);
        setPreviewTitle(file.name || file.url!.substring(file.url!.lastIndexOf('/') + 1));
    };

    // Utility function to convert image to base64
    const getBase64 = (file: File): Promise<string> =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = error => reject(error);
        });


    // Handle form submit
    const handleAddFt = async (values: any) => {
        try {
            const formData = new FormData();

            formData.append('fp_pattern', values.fp_pattern);
            formData.append('fp_parent', values.fp_parent);

            if (imageFile) {
                formData.append('attachments', imageFile); // ✅ binary file
            }

            if (initialValues?.fp_id) {
                formData.append('fp_id', initialValues.fp_id.toString());
            }
            const url = isEditMode
                ? 'edit-fabric-pattern'
                : 'add-fabric-pattern';

            const method = isEditMode ? 'put' : 'post';

            const data = await apiRequest({
                method,
                url,
                data: formData,
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            console.log(formData);

            onCreate(data); // Notify parent about success
            showSuccess(
                isEditMode ? 'Fabric Pattern updated successfully!' : 'Fabric Pattern created successfully!',
            );
            form.resetFields();
             if (!isModalClose) {
                setImageFile(null);
                setProfileImageFileList([]);
            }

            if (isModalClose) {
                onCancel(); // Close modal only if Submit button was clicked (not Submit & Create New)
            }
        } catch (error) {
            showError(`Failed to ${isEditMode ? 'update' : 'create'} fabric Pattern`);
        }
    };

    // When modal opens or mode changes, set/reset form fields accordingly
    useEffect(() => {
        if (visible && isEditMode && initialValues) {
            form.setFieldsValue({
                fp_pattern: initialValues.fp_pattern,
                fp_parent: initialValues.fp_parent,
            });
            if (initialValues.fp_image) {
                const imageUrl = `${initialValues.fp_image}`;
                setProfileImageFileList([
                    {
                        uid: '-1',
                        name: 'image.png',
                        status: 'done',
                        url: imageUrl,
                    },
                ]);
                setImageFile(null); // prevent old file being reused
            }
        } else if (visible) {
            form.resetFields();
        }
    }, [visible, isEditMode, initialValues, form]);


    const uploadButton = (
        <div
            style={{
                width: 220,
                height: 220,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            <PlusOutlined style={{ fontSize: 24 }} />
            <div style={{ marginTop: 8 }}>Upload</div>
        </div>
    );

    return (
        <>
            <Modal
                title={isEditMode ? 'Edit Fabric Pattern' : 'Add New Fabric Pattern'}
                visible={visible}
                onCancel={onCancel}
                footer={null}
                width="90%"
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleAddFt}
                    autoComplete="off"
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="fp_parent"
                                label={
                                    <span>
                                        Fabric Pattern Group &nbsp;
                                    </span>
                                }
                                rules={[{ required: true, message: 'Please select fabric pattern group' }]}
                            >
                                <Select
                                    placeholder="Select Organization"
                                    options={fabricpatterndata.map(fp => ({ label: fp.fp_pattern, value: fp.fp_id }))}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="fp_pattern"
                                className='px-8'
                                label={
                                    <span>
                                        Fabric Pattern &nbsp;
                                        <span style={{ fontSize: '12px', color: '#888' }}>
                                            (Max 100 characters)
                                        </span>
                                    </span>
                                }
                                rules={[
                                    { required: true, message: 'Please enter the Fabric Pattern Name' },
                                    { max: 100, message: 'Maximum 100 characters allowed' },
                                ]}
                            >
                                <Input
                                    showCount
                                    maxLength={100}
                                    placeholder="e.g., Fabric Pattern "

                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item label="Upload Fabric Pattern Image" required>
                                <Upload
                                    beforeUpload={() => false}
                                    listType="picture-card"
                                    maxCount={1}
                                    fileList={profileImageFileList}
                                    onPreview={handlePreview} // 👈 Add this
                                    onChange={({ fileList }) => {
                                        setProfileImageFileList(fileList);

                                        const rawFile = fileList[0]?.originFileObj;
                                        if (rawFile instanceof File) {
                                            setImageFile(rawFile); // ✅ set real File here
                                        } else {
                                            setImageFile(null);
                                        }
                                    }}
                                >
                                    {profileImageFileList.length >= 1 ? null : uploadButton}
                                </Upload>
                            </Form.Item>
                        </Col>
                    </Row>


                    <Form.Item style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Space>
                            <Button onClick={onCancel}>Cancel</Button>
                            {!isEditMode && <Button
                                type="primary"
                                htmlType="submit"
                                onClick={() => setIsModalClose(false)}
                                disabled={isSubmitDisabled()}
                            >
                                Submit & Create New Fabric Pattern
                            </Button>}
                            <Button
                                type="primary"
                                htmlType="submit"
                                onClick={() => setIsModalClose(true)}
                                disabled={isSubmitDisabled()}
                            >
                                Submit
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
            <Modal
                visible={previewOpen}
                title={previewTitle}
                footer={null}
                centered
                onCancel={() => setPreviewOpen(false)}
            >
                <img
                    alt="Preview"
                    style={{ width: '100%' }}
                    src={previewImage}
            />
            </Modal>
        </>
    );
};

export default FtModal;
